import { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Radio, ShieldCheck } from "lucide-react";
import { DbLead } from "@/lib/types";
import { sanitizeBroadcastCareNeeds } from "@phi";

interface Props {
  lead: DbLead | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * Shows the agency owner exactly what other agencies will see BEFORE the
 * lead is broadcast. The client name and any direct identifiers are stripped
 * — only ZIP, care needs, payer, hours, and urgency are shared. This matches
 * the columns persisted to `community_broadcasts`.
 */
export default function BroadcastConfirmModal({ lead, open, onClose, onConfirm }: Props) {
  const [submitting, setSubmitting] = useState(false);

  if (!lead) return null;

  const sanitizedCareNeeds = sanitizeBroadcastCareNeeds(lead.acuity_notes);

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="h-4 w-4 text-primary" />
            Refer to the community?
          </DialogTitle>
          <DialogDescription>
            Other agencies in the community will see the anonymized details below
            and can offer to take this family. They will not see the client's name.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/40 p-3 text-sm space:y-1.5 space-y-1.5">
          <Row label="ZIP" value={lead.zip_code || "—"} />
          <Row label="Care needs" value={sanitizedCareNeeds} />
          <Row label="Payer" value={lead.payer_source || "—"} />
          <Row label="Hours / week" value={lead.hours_needed ? String(lead.hours_needed) : "—"} />
          <Row label="Urgency" value={lead.urgency != null ? `${lead.urgency}/5` : "—"} />
        </div>

        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-success" />
          The client's name and contact info stay private to your agency. You can close
          the broadcast at any time from the community board.
        </p>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={submitting} className="gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />}
            Broadcast to community
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground text-right">{value}</span>
    </div>
  );
}